import { useState } from "react";
import { AlertTriangle, Download, Link2, Mail, RefreshCw, Star, Upload, Trash2 } from "lucide-react";

import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";

export default function AdminMaintenance() {
  const [cleanupResult, setCleanupResult] = useState(null);
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [snapshotBusy, setSnapshotBusy] = useState(false);
  const [restoreBusy, setRestoreBusy] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const [snapshotJson, setSnapshotJson] = useState("");
  const [error, setError] = useState("");
  const [digestBusy, setDigestBusy] = useState(false);
  const [digestResult, setDigestResult] = useState(null);
  // Add-from-link power-user tool
  const [linkUrl, setLinkUrl] = useState("");
  const [linkFeature, setLinkFeature] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkResult, setLinkResult] = useState(null);

  const addFromLink = async () => {
    if (!linkUrl.trim()) { setError("Paste a URL first."); return; }
    setLinkBusy(true); setError(""); setLinkResult(null);
    try {
      const { data } = await api.post("/admin/events/from-link", {
        url: linkUrl.trim(),
        status: "approved",
        feature: linkFeature,
      });
      setLinkResult(data);
      setLinkUrl("");
    } catch (e) {
      setError(
        e.response?.data?.detail ||
        "Something went wrong extracting that link. Please try again."
      );
    } finally { setLinkBusy(false); }
  };

  const sendDigest = async (dryRun) => {
    if (!dryRun && !window.confirm("Send the daily digest email to all configured recipients right now?")) return;
    setDigestBusy(true); setError(""); setDigestResult(null);
    try {
      const { data } = await api.post(`/admin/digest/send-now?dry_run=${dryRun ? "true" : "false"}`);
      setDigestResult(data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setDigestBusy(false); }
  };

  const runCleanup = async () => {
    if (!window.confirm("Delete past events, dummies, and orphans from THIS environment?")) return;
    setCleanupBusy(true); setError(""); setCleanupResult(null);
    try {
      const { data } = await api.post("/admin/cleanup");
      setCleanupResult(data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setCleanupBusy(false); }
  };

  const downloadSnapshot = async () => {
    setSnapshotBusy(true); setError("");
    try {
      const { data } = await api.get("/admin/snapshot");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.download = `smq-snapshot-${ts}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setSnapshotBusy(false); }
  };

  const restoreSnapshot = async () => {
    if (!snapshotJson.trim()) { setError("Paste a snapshot JSON first"); return; }
    if (!window.confirm("This will WIPE the existing events / sources / towns / categories in THIS environment and replace them with the snapshot. Continue?")) return;
    setRestoreBusy(true); setError(""); setRestoreResult(null);
    try {
      const payload = JSON.parse(snapshotJson);
      const { data } = await api.post("/admin/restore", { payload, wipe_first: true });
      setRestoreResult(data);
      setSnapshotJson("");
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setRestoreBusy(false); }
  };

  const mergeSnapshot = async () => {
    if (!snapshotJson.trim()) { setError("Paste a snapshot JSON first"); return; }
    if (!window.confirm("Merge this snapshot into THIS environment? Existing events/sources/towns/categories with the same _id will be overwritten; anything on this env NOT in the snapshot is kept.")) return;
    setRestoreBusy(true); setError(""); setRestoreResult(null);
    try {
      const payload = JSON.parse(snapshotJson);
      const { data } = await api.post("/admin/merge", { payload });
      setRestoreResult(data);
      setSnapshotJson("");
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setRestoreBusy(false); }
  };

  const onFile = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    setSnapshotJson(await file.text());
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-display font-bold">Maintenance</h1>
        <p className="text-sm text-slate-500 mt-1">Cleanup + cross-environment data sync tools.</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-md px-3 py-2" data-testid="maintenance-error">
          {error}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><Link2 size={16} /> Add event from link</CardTitle>
          <CardDescription>
            Paste an Instagram / Facebook / Eventbrite / venue-website URL and Claude
            extracts the event details + cover image. Publishes straight to the calendar.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <input
            type="url"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://www.instagram.com/p/... or https://www.eventbrite.com/e/..."
            data-testid="link-url-input"
            className="w-full text-sm border border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#1A3C5A]"
          />
          <label className="flex items-center gap-2 text-xs text-slate-600 select-none cursor-pointer">
            <Checkbox
              checked={linkFeature}
              onCheckedChange={(v) => setLinkFeature(v === true)}
              data-testid="link-feature-toggle"
            />
            <Star size={12} className="text-amber-500" />
            Also feature this event on the homepage
          </label>
          <Button onClick={addFromLink} disabled={linkBusy || !linkUrl.trim()} data-testid="link-submit-btn">
            {linkBusy ? <RefreshCw className="animate-spin" size={14} /> : <Link2 size={14} />}
            <span className="ml-2">{linkBusy ? "Extracting…" : "Extract + publish"}</span>
          </Button>
          {linkResult && linkResult.count > 0 && (
            <div
              data-testid="link-result"
              className="text-sm bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 space-y-1"
            >
              <div className="font-display font-semibold text-emerald-800">
                ✓ Added {linkResult.count} event{linkResult.count === 1 ? "" : "s"}
                {linkFeature ? " (featured)" : ""}
              </div>
              {(linkResult.created || []).map((e) => (
                <div key={e.id} className="text-xs text-emerald-900">
                  · {e.title} — {e.start_datetime?.slice(0, 16).replace("T", " ")}
                </div>
              ))}
            </div>
          )}
          {linkResult && linkResult.count === 0 && (
            <div
              data-testid="link-result"
              className="text-sm bg-amber-50 border border-amber-200 rounded-md px-3 py-2 space-y-1"
            >
              <div className="font-display font-semibold text-amber-800">
                Already on the calendar — nothing new to add.
              </div>
              {(linkResult.existing || []).map((e) => (
                <div key={e.id} className="text-xs text-amber-900">
                  · {e.title} — {e.start_datetime?.slice(0, 16).replace("T", " ")}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><Mail size={16} /> Daily digest email</CardTitle>
          <CardDescription>Sends a summary of pending events every morning at 8 AM Eastern to the configured recipients. Use these buttons to test / send on-demand.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex gap-2">
            <Button onClick={() => sendDigest(true)} disabled={digestBusy} variant="outline" data-testid="digest-dry-run-btn">
              {digestBusy ? <RefreshCw className="animate-spin" size={14} /> : <Mail size={14} />}
              <span className="ml-2">Dry-run (preview)</span>
            </Button>
            <Button onClick={() => sendDigest(false)} disabled={digestBusy} data-testid="digest-send-btn">
              {digestBusy ? <RefreshCw className="animate-spin" size={14} /> : <Mail size={14} />}
              <span className="ml-2">Send digest now</span>
            </Button>
          </div>
          {digestResult && (
            <pre className="text-xs bg-slate-50 border border-border rounded-md p-3 mt-2 font-mono" data-testid="digest-result">
              {JSON.stringify(digestResult, null, 2)}
            </pre>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><Trash2 size={16} /> Cleanup</CardTitle>
          <CardDescription>Deletes past events (&gt;6h old), events with no source_id, and events whose source has been deleted.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Button onClick={runCleanup} disabled={cleanupBusy} data-testid="cleanup-btn">
            {cleanupBusy ? <RefreshCw className="animate-spin" size={14} /> : <Trash2 size={14} />}
            <span className="ml-2">Run cleanup</span>
          </Button>
          {cleanupResult && (
            <pre className="text-xs bg-slate-50 border border-border rounded-md p-3 mt-2 font-mono" data-testid="cleanup-result">
              {JSON.stringify(cleanupResult, null, 2)}
            </pre>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><Download size={16} /> Download snapshot</CardTitle>
          <CardDescription>Export events, sources, towns, and categories from THIS environment as JSON.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={downloadSnapshot} disabled={snapshotBusy} data-testid="snapshot-btn">
            {snapshotBusy ? <RefreshCw className="animate-spin" size={14} /> : <Download size={14} />}
            <span className="ml-2">Download JSON snapshot</span>
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><Upload size={16} /> Restore / Merge snapshot</CardTitle>
          <CardDescription className="flex items-start gap-1.5">
            <AlertTriangle size={14} className="text-amber-600 mt-0.5 shrink-0" />
            <span><strong>Merge</strong> upserts by _id (safe for prod — nothing existing gets deleted). <strong>Restore</strong> wipes and replaces everything (use only on a fresh env).</span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <input type="file" accept="application/json,.json" onChange={onFile} className="text-sm" data-testid="restore-file-input" />
          <textarea
            className="w-full h-40 border border-border rounded-md p-2 text-xs font-mono"
            placeholder="…or paste snapshot JSON here"
            value={snapshotJson}
            onChange={e => setSnapshotJson(e.target.value)}
            data-testid="restore-json-input"
          />
          <div className="flex flex-wrap gap-2">
            <Button onClick={mergeSnapshot} disabled={restoreBusy || !snapshotJson.trim()} data-testid="merge-btn">
              {restoreBusy ? <RefreshCw className="animate-spin" size={14} /> : <Upload size={14} />}
              <span className="ml-2">Merge (safe — upsert by _id)</span>
            </Button>
            <Button onClick={restoreSnapshot} disabled={restoreBusy || !snapshotJson.trim()} variant="outline" data-testid="restore-btn">
              {restoreBusy ? <RefreshCw className="animate-spin" size={14} /> : <AlertTriangle size={14} />}
              <span className="ml-2">Restore (wipes first)</span>
            </Button>
          </div>
          {restoreResult && (
            <pre className="text-xs bg-slate-50 border border-border rounded-md p-3 mt-2 font-mono" data-testid="restore-result">
              {JSON.stringify(restoreResult, null, 2)}
            </pre>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
